import EditIcon from "@mui/icons-material/Edit";
import {
  Avatar,
  Chip,
  IconButton,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import moment from "moment";
import React, { useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import appcolors from "../../constants/Colors";
import { CURRENCY, DEFAULT_VAT_PERCENTAGE, PACKAGE_USAGE_TYPE } from "../../constants/Variables";
import {
  calculateProductFinalPrice,
  calculateVat,
  roundTo2Decimal,
} from "../../utils";
import GlobalButton from "../CustomButton";
import VariantsDialog from "../Dialogs/VariantsDialog";
import ProductDetailDialog from "./../Dialogs/ProductDetailDialog";
import DeleteIcon from "@mui/icons-material/Delete";
import ActionsMenu from "./ActionsMenu";

const tableStyle = {
  "&.MuiTableContainer-root .MuiTableHead-root": {
    backgroundColor: appcolors.primary,
  },
  "&.MuiTableContainer-root .MuiTableHead-root .MuiTableRow-root .MuiTableCell-root":
  {
    padding: "6px 16px",
    color: appcolors.white,
    fontSize: "1rem",
  },
  "& .view_btn": {
    border: "1px solid grey",
    height: 35,
    width: 75,
    fontSize: "0.875rem",
    borderRadius: "6px",
  },
};

export default function ProductTable({
  minWidth,
  tableHeader,
  tableData,
  displayRows,
  showEdit,
  handleEdit,
  showPagination = true,
  handleChangeProductStatus,
  paginatedData,
  handleChangeFeaturedStatus,
  onPageChange,
  onRowsPerPageChange,
  handleChangeVerifiedStatus,
  showActions,
  showDelete,
  handleDelete,
  showProductStatus,
  showMegaDiscount,
  handleChangeMegaDiscountStatus,
}) {
  const { t } = useTranslation(["products", "common"]);
  const openDetailRef = useRef(null);
  const openVariantsRef = useRef(null);
  const { currentLanguage } = useSelector((state) => state.languageReducer);
  const settings = useSelector((state) => state.settingsReducer.settings);

  return (
    <>
      <TableContainer sx={{ ...tableStyle, overflowX: "auto" }}>
        <Table
          sx={{
            minWidth: minWidth ? minWidth : "900px",
          }}
        >
          <TableHead>
            <TableRow>
              {tableHeader?.map((header) => (
                <TableCell key={header}>{t(header)}</TableCell>
              ))}

              {showActions && (
                <TableCell sx={{
                  position: "sticky",
                  right: 0,
                  zIndex: 3,
                  px: 0,
                  backgroundColor: "primary.main",
                }}>
                  {t("table.actions")}
                </TableCell>
              )}

            </TableRow>
          </TableHead>
          <TableBody>
            {
              // (paginatedData?.limit > 0
              //   ? tableData?.slice(
              //       paginatedData.page * paginatedData?.limit,
              //       paginatedData.page * paginatedData?.limit +
              //         paginatedData?.limit
              //     )
              //   : tableData
              // )

              tableData?.map((row, index) => {
                // This is calculated fot both the product and the base packages
                const finalProductPrice = calculateProductFinalPrice(
                  row.price,
                  row.discount,
                  row.profitMargin
                );

                // This is calculated for both the product and the base packages
                const productVat = calculateVat(
                  finalProductPrice,
                  settings?.tax || DEFAULT_VAT_PERCENTAGE
                );

                // This is calculated for both the product and the base packages
                const finalPriceAfterVat = roundTo2Decimal(
                  finalProductPrice + productVat
                );

                return (
                  <TableRow key={index}>
                    {displayRows.map((val) => (
                      <React.Fragment key={val}>
                        {val === "created_date" ? (
                          <TableCell>
                            {moment(row.createdAt).format("DD/MM/YYYY")}
                            <br />
                            {moment(row.createdAt).format("hh:mm:ss a")}
                          </TableCell>
                        ) : val === "product_sku" ? (
                          <TableCell>
                            {!row?.isVariant ? row.sku :
                              <GlobalButton
                                variant={"outlined"}
                                btnText={"view"}
                                className={"view_btn"}
                                styleProps={{
                                  height: 40,
                                  width: "auto",
                                }}
                                handleClickBtn={() => {
                                  openVariantsRef.current.openDialog({
                                    row: row,
                                  });
                                }}
                              />}
                          </TableCell>
                        ) : val === "category_name" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {row?.category?.name?.[currentLanguage]}
                          </TableCell>
                        ) : val === "sub_category_name" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {row?.subCategory?.length > 0 ? row?.subCategory.map((x) => (
                              <Typography variant="body2" mb={0.5}>
                                {x?.name?.[currentLanguage]}
                              </Typography>
                            )) : <Typography variant="body2" color="info.main" >{t("notAddedYet")}</Typography>}
                          </TableCell>
                        ) : val === "sub_sub_category_name" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {row?.subSubCategory?.length > 0 ? row?.subSubCategory.map((x) => (
                              <Typography variant="body2" mb={0.5}>
                                {x?.name?.[currentLanguage]}
                              </Typography>
                            )) : <Typography variant="body2" color="info.main" >{t("notAddedYet")}</Typography>}
                          </TableCell>
                        ) : val === "product_price" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {!row?.isVariant ? `${row.price} ${CURRENCY}` : "_"}
                          </TableCell>
                        ) : val === "product_profit_margin" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {!row?.isVariant ? `${row.profitMargin || 0}%` : "_"}
                          </TableCell>
                        ) : val === "product_discount" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {!row?.isVariant
                              ? `${row.discount} ${CURRENCY}`
                              : "_"}
                          </TableCell>
                        ) : val === "product_final_price" ? (
                          <TableCell sx={{ textTransform: "capitalize" }}>
                            {!row?.isVariant
                              ? `${row.finalPrice} ${CURRENCY}`
                              : "_"}
                          </TableCell>
                        ) : val === "store_name" ? (
                          <TableCell>
                            {row?.createdBy?.businessName || "-"}
                          </TableCell>
                        ) : val === "name_image" ? (
                          <TableCell>
                            <Stack
                              direction={"row"}
                              alignItems={"center"}
                              gap={1}
                              width={"400px"}
                            >
                              <Avatar
                                src={row.coverImage}
                                sx={{
                                  height: 55,
                                  width: 55,
                                  borderRadius: "6px",
                                  backgroundColor: "#edebeb",
                                  border: "1px solid #edebeb",
                                }}
                              />
                              <Tooltip title={row.name[currentLanguage]}>
                                <Typography
                                  variant={"body1"}
                                  textTransform={"capitalize"}
                                  sx={{
                                    width: "300px",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    WebkitLineClamp: 2,
                                    WebkitBoxOrient: "vertical",
                                    display: "-webkit-box",
                                  }}
                                >
                                  {row.name[currentLanguage]}
                                </Typography>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        ) : val === "product_initial_quantity" ? (
                          <TableCell>
                            {row.initialQuantity || 0}
                          </TableCell>
                        ) : val === "product_quantity" ? (
                          <TableCell>
                            {row.quantity || 0}
                          </TableCell>
                        ) : val === "product_size" ? (
                          <TableCell>
                            {row.size?.length > 0 ? (
                              <GlobalButton
                                variant={"outlined"}
                                btnText={"view"}
                                className={"view_btn"}
                                styleProps={{
                                  height: 40,
                                }}
                                handleClickBtn={() => {
                                  openDetailRef.current.openDialog({
                                    type: "size",
                                    row: row,
                                  });
                                }}
                              />
                            ) : (
                              "_"
                            )}
                          </TableCell>
                        ) : val === "product_color" ? (
                          <TableCell>
                            {row.color?.length > 0 ? (
                              <GlobalButton
                                variant={"outlined"}
                                btnText={"view"}
                                className={"view_btn"}
                                styleProps={{
                                  height: 40,
                                }}
                                handleClickBtn={() => {
                                  openDetailRef.current.openDialog({
                                    type: "color",
                                    row: row,
                                  });
                                }}
                              />
                            ) : (
                              "_"
                            )}
                          </TableCell>
                        ) : val === "product_size_with_color" ? (
                          <TableCell>
                            {row.sizeWithColor?.length > 0 ? (
                              <GlobalButton
                                variant={"outlined"}
                                btnText={"view"}
                                className={"view_btn"}
                                styleProps={{
                                  height: 40,
                                }}
                                handleClickBtn={() => {
                                  openDetailRef.current.openDialog({
                                    type: "sizeWithColor",
                                    row: row,
                                  });
                                }}
                              />
                            ) : (
                              "_"
                            )}
                          </TableCell>
                        ) : val === "product_weight" ? (
                          <TableCell>
                            {row.weigth?.length > 0 ? (
                              <GlobalButton
                                variant={"outlined"}
                                btnText={"view"}
                                className={"view_btn"}
                                styleProps={{
                                  height: 40,
                                }}
                                handleClickBtn={() => {
                                  openDetailRef.current.openDialog({
                                    type: "weigth",
                                    row: row,
                                  });
                                }}
                              />
                            ) : (
                              "_"
                            )}
                          </TableCell>
                        ) : val === "product_variants" ? (
                          <TableCell>
                            {row?.isVariant ? (
                              <GlobalButton
                                variant={"outlined"}
                                btnText={"view"}
                                className={"view_btn"}
                                styleProps={{
                                  height: 40,
                                }}
                                handleClickBtn={() => {
                                  openVariantsRef.current.openDialog({
                                    row: row,
                                  });
                                }}
                              />
                            ) : (
                              "_"
                            )}
                          </TableCell>
                        ) : val === "product_rating" ? (
                          <TableCell>{row.rating.toFixed(0)}</TableCell>
                        ) : val === "product_status" ? (
                          <TableCell>
                            <Switch
                              checked={row.status}
                              onChange={(e) =>
                                handleChangeProductStatus(e.target.checked, row)
                              }
                              inputProps={{ "aria-label": "controlled" }}
                            />
                          </TableCell>
                        ) : val === "featured_status" ? (
                          <TableCell>
                            <Switch
                              checked={row?.isFeatured}
                              onChange={(e) =>
                                handleChangeFeaturedStatus(
                                  e.target.checked,
                                  row
                                )
                              }
                              inputProps={{ "aria-label": "controlled" }}
                            />
                          </TableCell>
                        ) : val === "app_vat" ? (
                          <TableCell>
                            {!row?.isVariant ? `${settings?.tax || DEFAULT_VAT_PERCENTAGE}%` : "_"}
                          </TableCell>
                        ) : val === "product_final_price_after_vat" ? (
                          <TableCell>{!row?.isVariant ? `${finalPriceAfterVat} ${CURRENCY}` : "_"}</TableCell>
                        ) : val === "product_vat_amount" ? (
                          <TableCell>{!row?.isVariant ? `${productVat} ${CURRENCY}` : "_"}</TableCell>
                        ) : val === "package_price" ? (
                          <TableCell>{`${row.price} ${CURRENCY}`}</TableCell>
                        ) : val === "package_final_price" ? (
                          <TableCell>{`${row.finalPrice} ${CURRENCY}`}</TableCell>
                        ) : val === "package_vat_amount" ? (
                          <TableCell>{`${productVat} ${CURRENCY}`}</TableCell>
                        ) : val === "package_final_price_after_vat" ? (
                          <TableCell>{`${finalPriceAfterVat} ${CURRENCY}`}</TableCell>
                        ) : val === "menu_actions" ? (
                          <TableCell
                            sx={{
                              position: "sticky",
                              right: 0,
                              backgroundColor: "secondary.light",
                              zIndex: 3,
                              minWidth: 50,
                              boxShadow: "inset 4px 0 8px rgba(0,0,0,0.08)",
                            }}
                          >
                            <ActionsMenu
                              row={row}
                              showEdit={showEdit}
                              showDelete={showDelete}
                              showProductStatus={showProductStatus}
                              showMegaDiscount={showMegaDiscount}
                              handleEdit={handleEdit}
                              handleDelete={handleDelete}
                              showFeaturedStatus={true}
                              handleChangeProductStatus={handleChangeProductStatus}
                              handleChangeFeaturedStatus={handleChangeFeaturedStatus}
                              handleChangeMegaDiscountStatus={handleChangeMegaDiscountStatus}
                            />
                          </TableCell>
                        ) : val === "actions" ? (
                          <TableCell
                            sx={{
                              position: "sticky",
                              right: 0,
                              backgroundColor: "secondary.light",
                              zIndex: 2,
                              minWidth: 50,
                              boxShadow: "inset 4px 0 8px rgba(0,0,0,0.08)",

                            }}
                          >
                            <Stack direction={"row"} alignItems={"center"} justifyContent={"center"}>
                              {showMegaDiscount && (
                                <Switch
                                  checked={row.isMegaDiscount}
                                  onChange={(e) =>
                                    handleChangeMegaDiscountStatus(e.target.checked, row)
                                  }
                                  inputProps={{ "aria-label": "controlled" }}
                                />
                              )}
                              {showProductStatus && (
                                <Switch
                                  checked={row.status}
                                  onChange={(e) =>
                                    handleChangeProductStatus(e.target.checked, row)
                                  }
                                  inputProps={{ "aria-label": "controlled" }}
                                />
                              )}
                              {showEdit && (
                                <IconButton onClick={() => handleEdit(row)}>
                                  <EditIcon
                                    sx={{ fontSize: 18, color: "primary.main" }}
                                  />
                                </IconButton>
                              )}
                              {showDelete && (
                                <IconButton onClick={() => handleDelete(row)}>
                                  <DeleteIcon
                                    sx={{ fontSize: 18, color: "error.main" }}
                                  />
                                </IconButton>
                              )}

                            </Stack>
                          </TableCell>
                        ) : (
                          <TableCell>
                            <Typography
                              lineHeight="normal"
                              fontSize={"0.875rem"}
                            >
                              {row[val]}
                            </Typography>
                          </TableCell>
                        )}
                      </React.Fragment>
                    ))}
                  </TableRow>
                );
              })
            }
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <TablePagination
          rowsPerPageOptions={[10, 20, 50, 100]}
          component="div"
          count={paginatedData.total}
          rowsPerPage={paginatedData.limit}
          page={paginatedData.page - 1}
          onPageChange={onPageChange}
          onRowsPerPageChange={onRowsPerPageChange}
        />
      )}

      <VariantsDialog ref={openVariantsRef} />
      <ProductDetailDialog ref={openDetailRef} />
    </>
  );
}
